import type { ReactElement } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import type { PublishJobProgress } from '@course-authoring/shared-types';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

export interface PublishJobProgressBarProps {
  stepLabel: string;
  progress: PublishJobProgress;
  unit?: string;
}

/** A compact determinate progress bar shared by preparation surfaces. */
export function PublishJobProgressBar({ stepLabel, progress, unit = 'items' }: PublishJobProgressBarProps): ReactElement | null {
  const { tokens } = useTheme();
  const prefersReducedMotion = usePrefersReducedMotion();
  if (progress.total <= 0) return null;

  const completed = Math.max(0, Math.min(progress.completed, progress.total));
  const percentage = Math.round((completed / progress.total) * 100);
  return (
    <div
      role="progressbar"
      aria-label={`${stepLabel} progress`}
      aria-valuemin={0}
      aria-valuemax={progress.total}
      aria-valuenow={completed}
      aria-valuetext={`${completed} of ${progress.total} ${unit}`}
      style={{
        width: '100%',
        height: 6,
        marginTop: tokens.spacing.xs,
        borderRadius: 3,
        overflow: 'hidden',
        background: `color-mix(in srgb, ${tokens.ui.fixedAccent} 14%, ${tokens.backgrounds.page})`,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: `${percentage}%`,
          height: '100%',
          borderRadius: 3,
          background: tokens.ui.focusRing,
          transition: prefersReducedMotion ? 'none' : 'width 300ms ease',
        }}
      />
    </div>
  );
}
